// The lesson's practice as the server sends it (GET /learn/lectures/{id}/practices)
// and what a learner sends back (POST /learn/practices/{id}/submit), with the
// small readers the page needs: splitting a sentence at its {{id}} tokens, and
// telling an answered question from one still waiting.
//
// Nothing here can know the right answer: the server never sends it. A check
// says which questions were right, the score, and whether the practice is
// passed — every answer right, the rule the lesson's gate counts by.

import type { Block } from '../../../components/Blocks'

export type PracticeKind = 'multiple_choice' | 'single_choice' | 'fill_blanks' | 'select_options'

export type LearnerOption = { id: string; text: string }

export type LearnerQuestion = {
  id: string
  kind: PracticeKind
  text?: string
  text_with_gaps?: string
  text_with_selects?: string
  options?: LearnerOption[]
  gaps?: { id: string }[]
  selects?: { id: string; options: LearnerOption[] }[]
}

export type AttemptSummary = {
  id: string
  correct: number
  total: number
  score_pct: number
  created_at: string
}

/** One block of the information given with a practice, resolved like a lesson's own. */
export type Attachment = { id: string; kind: Block['kind']; payload: unknown }

export type LearnerPractice = {
  id: string
  lecture_id: string
  kind: PracticeKind
  prompt: string
  instruction_html: string
  attachments: Attachment[]
  questions: LearnerQuestion[]
  seq: number
  /** Some attempt had every answer right. */
  passed: boolean
  last_attempt?: AttemptSummary
}

export type AttemptResult = {
  attempt_id: string
  correct: number
  total: number
  score_pct: number
  passed: boolean
  questions: { question_id: string; correct: boolean }[]
}

/** A learner's answer to one question while it is on screen. */
export type Answer = { selected: string[]; gaps: Record<string, string> }

export const NO_ANSWER: Answer = { selected: [], gaps: {} }

/** An attachment as the lesson's block renderer takes it. */
export function asBlock(a: Attachment, seq: number): Block {
  return { id: a.id, kind: a.kind, seq, payload: a.payload } as Block
}

export type SentencePiece = { kind: 'text'; text: string } | { kind: 'slot'; id: string }

const TOKEN = /\{\{([^{}]+)\}\}/g

/**
 * A sentence split at its tokens. A token naming no gap or list of the
 * question stays as text: the server refuses such a question, so it can only
 * be an old row, and showing the braces beats losing the words.
 */
export function splitSentence(text: string, slots: ReadonlySet<string>): SentencePiece[] {
  const out: SentencePiece[] = []
  let last = 0
  for (const m of text.matchAll(TOKEN)) {
    const id = m[1]!
    if (!slots.has(id)) continue
    if (m.index > last) out.push({ kind: 'text', text: text.slice(last, m.index) })
    out.push({ kind: 'slot', id })
    last = m.index + m[0].length
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) })
  return out
}

/** The order a drop-down list shows its options in: alphabetical, as the staff preview shows them. */
export function displayOrder(options: LearnerOption[]): LearnerOption[] {
  return [...options].sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: 'base' }))
}

/** The gaps or lists of a sentence question that are on screen, in the order they stand. */
export function slotsOf(q: LearnerQuestion): string[] {
  const gaps = q.kind === 'fill_blanks'
  const ids = new Set(gaps ? (q.gaps ?? []).map((g) => g.id) : (q.selects ?? []).map((s) => s.id))
  const text = (gaps ? q.text_with_gaps : q.text_with_selects) ?? ''
  return splitSentence(text, ids).flatMap((p) => (p.kind === 'slot' ? [p.id] : []))
}

/**
 * Whether a question has an answer in every place it asks for one — every
 * place the learner can see. A gap or list whose token an old row's sentence
 * lacks is not on screen, could never be filled in, and is not graded either
 * (the server marks only the places its sentence shows): it must not keep its
 * question from being sent. A sentence that shows none at all asks nothing,
 * and counts as answered as it stands.
 */
export function isAnswered(q: LearnerQuestion, given: Answer | undefined): boolean {
  const a = given ?? NO_ANSWER
  switch (q.kind) {
    case 'single_choice':
      return a.selected.length === 1
    case 'multiple_choice':
      return a.selected.length > 0
    case 'fill_blanks':
      return slotsOf(q).every((id) => (a.gaps[id] ?? '').trim() !== '')
    case 'select_options':
      return slotsOf(q).every((id) => (a.gaps[id] ?? '') !== '')
  }
}

/**
 * The submission body: each question's answer in the field its kind is read
 * from. A sentence question sends the places on screen and nothing else.
 */
export function submission(questions: LearnerQuestion[], answers: Record<string, Answer>) {
  return {
    answers: questions.map((q) => {
      const a = answers[q.id] ?? NO_ANSWER
      if (q.kind === 'fill_blanks' || q.kind === 'select_options') {
        const shown = new Set(slotsOf(q))
        return { question_id: q.id, gaps: Object.fromEntries(Object.entries(a.gaps).filter(([id]) => shown.has(id))) }
      }
      return { question_id: q.id, selected: a.selected }
    }),
  }
}
